import { useState } from 'react';
import {
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragStartEvent,
  DragEndEvent,
} from '@dnd-kit/core';
import { sortableKeyboardCoordinates } from '@dnd-kit/sortable';
import { arrayMove } from '@dnd-kit/sortable';
import { produksjonAPI } from '@/services/api';
import { isPlanRadSortable } from '@/lib/planDndUtils';
import type { PlanMalElement } from './usePlanMalDialog';

interface UsePlanMalDragDropProps {
  kategoriId: number;
  elements: PlanMalElement[];
  setElements: (elements: PlanMalElement[]) => void;
  expandedHeaders: Set<number>;
  setExpandedHeaders: (headers: Set<number> | ((prev: Set<number>) => Set<number>)) => void;
  onSave: () => void;
  onError: (error: string) => void;
  onSuccess: (message: string) => void;
  fetchPlanMal: () => Promise<void>;
}

export const usePlanMalDragDrop = ({
  kategoriId,
  elements,
  setElements,
  expandedHeaders,
  setExpandedHeaders,
  onSave,
  onError,
  onSuccess,
  fetchPlanMal,
}: UsePlanMalDragDropProps) => {
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const [activeElement, setActiveElement] = useState<PlanMalElement | null>(null);

  const overskrifter = elements.filter(el => el.type === 'overskrift').sort((a, b) => a.rekkefølge - b.rekkefølge);

  const handleDragStart = (event: DragStartEvent) => {
    const { active } = event;
    const element = elements.find(el => el.id === active.id);
    setActiveElement(element || null);
    
    if (
      element &&
      isPlanRadSortable(element) &&
      element.parent_id != null
    ) {
      setExpandedHeaders(prev => {
        const newSet = new Set(prev);
        newSet.add(element.parent_id!);
        return newSet;
      });
    }
  };

  const handleDragOver = (event: any) => {
    const { over } = event;
    
    if (over?.id) {
      const overElement = elements.find(el => el.id === over.id);
      if (overElement?.type === 'overskrift') {
        setExpandedHeaders(prev => {
          const newSet = new Set(prev);
          newSet.add(overElement.id);
          return newSet;
        });
      }
    }
  };

  const handleDragCancel = () => {
    setActiveElement(null);
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    setActiveElement(null);

    if (!over || active.id === over.id) {
      return;
    }

    const activeElement = elements.find(el => el.id === active.id);
    const overElement = elements.find(el => el.id === over.id);

    if (!activeElement) return;

    // Håndter overskrift drag
    if (activeElement.type === 'overskrift' && overElement?.type === 'overskrift') {
      const oldIndex = overskrifter.findIndex(h => h.id === active.id);
      const newIndex = overskrifter.findIndex(h => h.id === over.id);

      if (oldIndex === -1 || newIndex === -1) return;

      const newOrder = arrayMove(overskrifter, oldIndex, newIndex);
    
      const updatedElements = elements.map(el => {
        if (el.type === 'overskrift') {
          const newPos = newOrder.findIndex(h => h.id === el.id);
          return { ...el, rekkefølge: newPos };
        }
        return el;
      });
      setElements(updatedElements);

      try {
        await Promise.all(
          newOrder.map((overskrift, index) =>
            produksjonAPI.updatePlanMalRekkefølge(kategoriId, overskrift.id, index)
          )
        );
        await fetchPlanMal();
        onSave();
      } catch (err) {
        console.error('Feil ved oppdatering av overskrift-rekkefølge:', err);
        onError('Kunne ikke oppdatere rekkefølge');
        await fetchPlanMal();
      }
      return;
    }

    // Håndter hendelse drag
    if (isPlanRadSortable(activeElement)) {
      let targetParentId: number | null = null;
      let targetIndex = 0;

      if (overElement && isPlanRadSortable(overElement)) {
        targetParentId = overElement.parent_id ?? null;
        const siblings = elements
          .filter(el => el.parent_id === targetParentId)
          .sort((a, b) => a.rekkefølge - b.rekkefølge);
        targetIndex = siblings.findIndex(h => h.id === over.id);
      } else if (overElement?.type === 'overskrift') {
        targetParentId = overElement.id;
        targetIndex = 0;
      }

      if (targetParentId === null) return;

      const oldParentId = activeElement.parent_id ?? null;

      if (oldParentId === targetParentId) {
        const siblings = elements.filter(el => el.parent_id === targetParentId).sort((a, b) => a.rekkefølge - b.rekkefølge);
        const oldIndex = siblings.findIndex(h => h.id === active.id);
        
        if (oldIndex === -1) return;
        
        const newOrder = arrayMove(siblings, oldIndex, targetIndex);
        
        const updatedElements = elements.map(el => {
          if (el.parent_id === targetParentId) {
            const newPos = newOrder.findIndex(h => h.id === el.id);
            return { ...el, rekkefølge: newPos };
          }
          return el;
        });
        setElements(updatedElements);

        try {
          await Promise.all(
            newOrder.map((hendelse, index) =>
              produksjonAPI.updatePlanMalRekkefølge(kategoriId, hendelse.id, index)
            )
          );
          await fetchPlanMal();
          onSave();
        } catch (err) {
          console.error('Feil ved oppdatering av rekkefølge:', err);
          onError('Kunne ikke oppdatere rekkefølge');
          await fetchPlanMal();
        }
      } else {
        try {
          await produksjonAPI.updatePlanMalElement(kategoriId, activeElement.id, {
            parentId: targetParentId,
            rekkefølge: targetIndex,
          });

          const newSiblings = elements
            .filter(el => el.parent_id === targetParentId && el.id !== activeElement.id)
            .sort((a, b) => a.rekkefølge - b.rekkefølge);
          newSiblings.splice(targetIndex, 0, { ...activeElement, parent_id: targetParentId });
          
          await Promise.all(
            newSiblings.map((hendelse, index) =>
              produksjonAPI.updatePlanMalRekkefølge(kategoriId, hendelse.id, index)
            )
          );

          await fetchPlanMal();
          onSave();
          onSuccess('Innslag flyttet');
        } catch (err) {
          console.error('Feil ved flytting av innslag:', err);
          onError('Kunne ikke flytte innslag');
          await fetchPlanMal();
        }
      }
    }
  };

  return {
    sensors,
    activeElement,
    handleDragStart,
    handleDragOver,
    handleDragCancel,
    handleDragEnd,
  };
};

